import { Link2, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { RelativesReviewDialog } from "@/components/RelativesReviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

export const RELATIVE_LINES = ["maternal", "paternal"];
export const RELATIVE_RELATIONSHIPS = ["brother", "sister", "half_brother", "half_sister", "offspring", "other"];
// keep in sync with backend horses/models.py DISCIPLINES
const DISCIPLINES = ["show_jumping", "dressage", "eventing", "racing", "morphology", "thoroughbred_racing", "polo", "trotters"];

const EMPTY = { line: "maternal", relationship: "other", name: "", sire: "", dam: "", discipline: "", level: "", result: "", url: "" };

/* Fase C — editor de Performance Relatives (líneas materna/paterna) del
   HorseForm único. Entrada manual siempre disponible + import IA desde
   imagen/PDF que SOLO propone un draft: nada se guarda sin confirmación. */
export const PerformanceRelativesEditor = ({ value, onChange }) => {
  const { t } = useTranslation();
  const aiRef = useRef(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [draft, setDraft] = useState(null); // {relatives, notes} — panel de revisión

  const setField = (i, key) => (v) =>
    onChange(value.map((r, j) => (j === i ? { ...r, [key]: v } : r)));

  const handleAiImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/horses/relatives/extract", fd, { timeout: 120000 });
      setDraft(data);
    } catch (err) {
      const code = err.response?.data?.detail;
      setAiError(
        code === "file_too_large" ? t("ai.fileTooLarge")
        : code === "unsupported_type" ? t("ai.unsupportedType")
        : code === "unreadable_document" ? t("pr.unreadable")
        : t("pr.aiFailed")
      );
    } finally {
      setAiLoading(false);
      if (aiRef.current) aiRef.current.value = "";
    }
  };

  const applyDraft = (entries) => {
    onChange([...value, ...entries]);
    setDraft(null);
    toast.success(t("pr.appliedToast", { count: entries.length }));
  };

  return (
    <div className="flex flex-col gap-3" data-testid="performance-relatives-editor">
      <div className="flex flex-col gap-0.5">
        <Label>{t("pr.title")}</Label>
        <span className="text-xs text-[#737373]">{t("pr.sectionHint")}</span>
      </div>

      {value.map((r, i) => (
        <div key={i} className="rounded-2xl border border-black/5 bg-[#F9F9F9] p-4 flex flex-col gap-3" data-testid={`pr-row-${i}`}>
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1.3fr_1.7fr_36px] gap-2 items-end">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("pr.line")}</span>
              <Select value={r.line} onValueChange={setField(i, "line")}>
                <SelectTrigger data-testid={`pr-line-${i}`} className="h-10 rounded-xl border-black/10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {RELATIVE_LINES.map((l) => (
                    <SelectItem key={l} value={l}>{t(`pr.${l}Line`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("pr.relationship")}</span>
              <Select value={r.relationship} onValueChange={setField(i, "relationship")}>
                <SelectTrigger data-testid={`pr-relationship-${i}`} className="h-10 rounded-xl border-black/10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {RELATIVE_RELATIONSHIPS.map((rel) => (
                    <SelectItem key={rel} value={rel}>{t(`pr.rel_${r.line}_${rel}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 col-span-2 sm:col-span-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("pr.name")}</span>
              <Input
                data-testid={`pr-name-${i}`}
                value={r.name}
                placeholder={t("pr.namePh")}
                onChange={(e) => setField(i, "name")(e.target.value)}
                className="h-10 rounded-xl border-black/10 bg-white"
              />
            </div>
            <button
              type="button"
              data-testid={`pr-remove-${i}`}
              aria-label={t("pr.remove")}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="h-9 w-9 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200 justify-self-end"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("pr.discipline")}</span>
              <Select value={r.discipline || "none"} onValueChange={(v) => setField(i, "discipline")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid={`pr-discipline-${i}`} className="h-10 rounded-xl border-black/10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="none">—</SelectItem>
                  {DISCIPLINES.map((d) => (
                    <SelectItem key={d} value={d}>{t(`disc.${d}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("pr.level")}</span>
              <Input data-testid={`pr-level-${i}`} value={r.level || ""} placeholder={t("pr.levelPh")}
                     onChange={(e) => setField(i, "level")(e.target.value)} className="h-10 rounded-xl border-black/10 bg-white" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("horse.sire")}</span>
              <Input data-testid={`pr-sire-${i}`} value={r.sire || ""}
                     onChange={(e) => setField(i, "sire")(e.target.value)} className="h-10 rounded-xl border-black/10 bg-white" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("horse.dam")}</span>
              <Input data-testid={`pr-dam-${i}`} value={r.dam || ""}
                     onChange={(e) => setField(i, "dam")(e.target.value)} className="h-10 rounded-xl border-black/10 bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_1fr] gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">{t("pr.result")}</span>
              <Input data-testid={`pr-result-${i}`} value={r.result || ""} placeholder={t("pr.resultPh")}
                     onChange={(e) => setField(i, "result")(e.target.value)} className="h-10 rounded-xl border-black/10 bg-white" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]/40">
                <Link2 className="w-3 h-3 inline mr-1 -mt-0.5" aria-hidden="true" />{t("pr.url")}
              </span>
              <Input data-testid={`pr-url-${i}`} type="url" value={r.url || ""} placeholder="https://"
                     onChange={(e) => setField(i, "url")(e.target.value)} className="h-10 rounded-xl border-black/10 bg-white" />
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="pr-add"
          onClick={() => onChange([...value, { ...EMPTY }])}
          className="inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          {t("pr.addRelative")}
        </button>
        <span className="text-black/15" aria-hidden="true">·</span>
        <input ref={aiRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleAiImport} data-testid="pr-ai-input" />
        <Button
          type="button"
          variant="ghost"
          data-testid="pr-ai-import"
          onClick={() => aiRef.current?.click()}
          disabled={aiLoading}
          className="h-9 px-4 rounded-full bg-[#1D4D3B]/[0.06] text-[#1D4D3B] hover:bg-[#1D4D3B]/10 transition-colors duration-300 text-sm"
        >
          {aiLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
              {t("ai.reading")}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" />
              {t("pr.aiImport")}
            </>
          )}
        </Button>
      </div>
      {aiError && (
        <span className="text-xs text-red-700" role="alert" data-testid="pr-ai-error">{aiError}</span>
      )}

      <RelativesReviewDialog draft={draft} onClose={() => setDraft(null)} onApply={applyDraft} />
    </div>
  );
};
